import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Modal, Tabs, Tag, Tooltip } from '@arco-design/web-react';
import MessageActionBar from './MessageActionBar';
import MessageEvidence from './MessageEvidence';
import MessageReviewForm from './MessageReviewForm';
import { CommandRequest, CommandResult, ReviewFields } from './types';
import { MessageApiError } from './api';
import {
  ActionButton,
  ReviewTab,
  ReviewUiState,
  aiColumnLabel,
  aiPhaseColors,
  deriveReviewUiState,
  fieldDiffs,
  formatBeijingTime,
  publicationColumnLabel,
  publicationStateColors,
  reasonHint,
  reasonIssue,
  reviewStateColors,
  reviewStateLabels,
  reviewToFields,
  statusColor
} from './reviewUiState';

export default function MessageReviewDrawer({
  visible,
  title,
  uiInput,
  ui,
  onClose,
  onRefresh,
  onRetryDetail,
  onRetryCommand,
  onRefreshCommand,
  onSave,
  onCommand,
  onChangeField,
  onAdoptLatest,
  onKeepCurrent,
  foreignOps,
  actionError,
  notice
}: {
  visible: boolean;
  title: string;
  uiInput: Parameters<typeof deriveReviewUiState>[0];
  ui: ReviewUiState;
  onClose: () => void;
  onRefresh: () => void;
  onRetryDetail: () => void;
  onRetryCommand: (contentId?: string) => void;
  onRefreshCommand: () => void;
  onSave: () => void;
  onCommand: (type: 'PUBLISH' | 'WITHDRAW' | 'UNBLOCK') => void;
  onChangeField: <K extends keyof ReviewFields>(name: K, value: ReviewFields[K]) => void;
  onAdoptLatest: () => void;
  onKeepCurrent: () => void;
  actionError: MessageApiError | null;
  notice: string;
  foreignOps: {
    contentId: string;
    result: CommandResult | null;
    retry: { contentId: string; body: CommandRequest } | null;
  }[];
}) {
  const detail = uiInput.detail;
  const [tab, setTab] = useState<ReviewTab | null>(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const activeTab = tab ?? ui.defaultTab;
  useEffect(() => {
    setTab(null);
  }, [uiInput.selectedId]);
  const diffs = useMemo(() => {
    if (!detail?.review) return [];
    return fieldDiffs(uiInput.fields, reviewToFields(detail.review));
  }, [detail, uiInput.fields]);

  const locate = () => {
    const issue = ui.articleIssues[0] || reasonIssue(uiInput.fields.reason);
    setTab('review');
    window.requestAnimationFrame(() => {
      const id =
        issue?.field === 'reason'
          ? 'review-field-reason'
          : issue?.itemIndex != null
            ? `review-field-${issue.field}-${issue.itemIndex}`
            : issue
              ? `review-field-${issue.field}`
              : 'review-group-basic';
      const node = document.getElementById(id);
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ block: 'center' });
      }
      if (node instanceof HTMLElement) {
        const focusable = node.matches('input,textarea,select,button') ? node : node.querySelector('input,textarea,select');
        (focusable as HTMLElement | null)?.focus();
      }
    });
  };

  const runAction = (action: ActionButton) => {
    switch (action.id) {
      case 'retry-load':
        onRetryDetail();
        break;
      case 'view-history':
        setTab('history');
        break;
      case 'view-processing':
      case 'view-evidence':
        setTab('evidence');
        break;
      case 'save':
        onSave();
        break;
      case 'publish':
      case 'publish-update':
        onCommand('PUBLISH');
        break;
      case 'edit-review':
        setTab('review');
        break;
      case 'unblock':
        onCommand('UNBLOCK');
        break;
      case 'confirm-result':
        if (uiInput.retry) onRetryCommand(uiInput.retry.contentId);
        else onRefreshCommand();
        break;
      case 'refresh-compare':
        onRefresh();
        setCompareOpen(true);
        setTab('review');
        break;
      default:
        break;
    }
  };

  const selectedTitle = title;
  return (
    <Drawer
      className="agent-message-drawer"
      wrapClassName="agent-message-drawer-wrap"
      visible={visible}
      width="min(1120px, 94vw)"
      title={
        <div className="message-drawer-title">
          <strong>快讯审核</strong>
          <Tooltip content={selectedTitle}>
            <span title={selectedTitle}>{selectedTitle}</span>
          </Tooltip>
          <Button aria-label="刷新版本并保留编辑" onClick={onRefresh} disabled={uiInput.writeBusy}>
            刷新状态
          </Button>
        </div>
      }
      footer={
        <MessageActionBar
          ui={ui}
          fields={uiInput.fields}
          reasonLocked={!detail?.capabilities.canWrite || uiInput.writeBusy}
          onReasonChange={value => onChangeField('reason', value)}
          onPrimary={runAction}
          onSecondary={runAction}
          onWithdraw={() => setWithdrawOpen(true)}
          onClose={onClose}
          onLocate={() => {
            locate();
          }}
        />
      }
      onCancel={onClose}
      unmountOnExit
    >
      <div className="message-drawer-frame">
        <section className="message-detail" aria-label={`正在查看：${selectedTitle}`}>
        <section className="message-drawer-status" aria-label="操作指引">
          {detail && (
            <p className="message-identity">
              快讯 {detail.id} · 来源 {detail.sourceId} · 来源时间 {formatBeijingTime(detail.text?.sourcePublishedAt)}{' '}
              <button
                type="button"
                className="message-copy-id"
                onClick={() => void navigator.clipboard?.writeText(detail.id)}
              >
                复制编号
              </button>
            </p>
          )}
          <div className="message-action-status">
            <Tag color={ui.visibilityKind === 'blocked' || ui.visibilityKind === 'source-deleted' ? 'red' : ui.visibilityKind.startsWith('visible') ? 'green' : 'gray'}>
              {ui.visibilityLabel}
            </Tag>
            {ui.progressLabel && <Tag color="orangered">{ui.progressLabel}</Tag>}
          </div>
          {detail?.states && (
            <div className="message-action-status" aria-label="当前业务状态">
              <Tag color={statusColor(detail.states.ai.phase, aiPhaseColors)}>{aiColumnLabel(detail.states.ai)}</Tag>
              <Tag color={statusColor(detail.states.review.state, reviewStateColors)}>
                {reviewStateLabels[detail.states.review.state]}
              </Tag>
              <Tag color={statusColor(detail.states.publication.state, publicationStateColors)}>
                {publicationColumnLabel(detail.states.publication)}
              </Tag>
            </div>
          )}
          {(detail?.states?.ai.reasonCode || detail?.states?.review.reasonCode || detail?.states?.publication.reasonCode) && (
            <p className="message-next-step">
              <strong>为什么：</strong>
              {reasonHint(detail.states.review.reasonCode || detail.states.publication.reasonCode || detail.states.ai.reasonCode)}
            </p>
          )}
          <p className="message-next-step">
            <strong>下一步：</strong>
            {ui.nextStep}
          </p>
          {detail && (
            <details className="message-meta-disclosure">
              <summary>查看快讯版本与发布信息</summary>
              <div>
                <p>快讯 {detail.id}</p>
                <p>
                  来源版本：{detail.sourceVersion} · 审核版本：{detail.editorialVersion}
                </p>
                {detail.publication && (
                  <p>
                    发布来源：{detail.publication.origin === 'AUTOMATIC' ? '自动发布' : '人工发布'} · 处理记录状态：
                    {detail.publication.status} · 发布版本：{detail.publication.publicationVersionId} · 发布时间：
                    {detail.publication.publishedAt}
                  </p>
                )}
                <p>
                  {detail.manualBlock ? '人工拦截中' : '未人工拦截'}
                  {detail.sourceDeleted ? ' · 来源已删除' : ''}
                </p>
              </div>
            </details>
          )}
        </section>
        {actionError && (
          <div role="alert">
            {actionError.status ? `错误码 ${actionError.status} · ` : ''}
            {actionError.message}
            {actionError.status === 409 && <p>数据版本已变化，编辑文字已保留。请刷新版本并核对后再操作。</p>}
          </div>
        )}
        {uiInput.retry && (
          <div className="message-notice" role="status">
            命令结果尚未确认 · 快讯 {uiInput.retry.contentId} · {uiInput.retry.body.type} · 原因：{uiInput.retry.body.reason}{' '}
            <Button disabled={uiInput.writeBusy} onClick={() => onRetryCommand(uiInput.retry?.contentId)}>
              重试原命令
            </Button>
            <p>重试复用原命令编号及全部参数，其他写操作暂停。</p>
          </div>
        )}
        {uiInput.receipt && (
          <div className="message-notice" role="status">
            快讯 {uiInput.receipt.contentId} ·{' '}
            {uiInput.receipt.result.status === 'PENDING'
              ? '等待处理（尚未完成）'
              : uiInput.receipt.result.status === 'COMPLETED'
                ? '命令已完成'
                : '命令被拒绝'}{' '}
            · {uiInput.receipt.result.resultCode || uiInput.receipt.result.commandId}
            <Button disabled={uiInput.writeBusy} onClick={onRefreshCommand}>
              刷新命令状态
            </Button>
          </div>
        )}
        {foreignOps.map(item => (
          <div key={item.contentId} className="message-notice" role="status">
            快讯 {item.contentId} 仍有未完成操作
            {item.result?.status === 'PENDING' ? ' · 等待处理（尚未完成）' : ''}
            {item.retry && (
              <Button disabled={uiInput.writeBusy} onClick={() => onRetryCommand(item.contentId)}>
                重试原命令
              </Button>
            )}
          </div>
        ))}
        {notice && <p role="status">{notice}</p>}
        {uiInput.detailLoading && !detail && <p role="status">正在加载详情…</p>}
        {uiInput.detailError && !detail && (
          <div role="alert">
            {uiInput.detailError.message}
            <Button onClick={onRetryDetail}>重试详情</Button>
          </div>
        )}
        {detail && (
            <Tabs
              activeTab={activeTab}
              onChange={value => setTab(value as ReviewTab)}
              destroyOnHide={false}
              lazyload={false}
              className="message-drawer-tabs"
            >
              <Tabs.TabPane key="review" title="审核内容">
                <div className="message-drawer-scroll">
                  <MessageReviewForm
                    detail={detail}
                    fields={uiInput.fields}
                    locked={ui.formLocked}
                    analysisV2={ui.analysisV2}
                    seedSource={uiInput.seedSource}
                    showErrors={uiInput.attempted}
                    issues={ui.articleIssues}
                    onChange={onChangeField}
                  />
                </div>
              </Tabs.TabPane>
              <Tabs.TabPane key="evidence" title="原文与AI依据">
                <div className="message-drawer-scroll">
                  <MessageEvidence detail={detail} pane="source" />
                </div>
              </Tabs.TabPane>
              <Tabs.TabPane key="publication" title="当前公开版本">
                <div className="message-drawer-scroll">
                  <MessageEvidence detail={detail} pane="publication" />
                </div>
              </Tabs.TabPane>
              <Tabs.TabPane key="history" title="操作记录">
                <div className="message-drawer-scroll">
                  <MessageEvidence detail={detail} pane="history" />
                </div>
              </Tabs.TabPane>
            </Tabs>
        )}
        </section>
      </div>
      <Modal
        title={detail?.publication?.visible ? '确认撤回并拦截' : '确认拦截发布'}
        visible={withdrawOpen}
        okText={detail?.publication?.visible ? '确认撤回' : '确认拦截'}
        cancelText="取消"
        onOk={() => {
          setWithdrawOpen(false);
          onCommand('WITHDRAW');
        }}
        onCancel={() => setWithdrawOpen(false)}
        unmountOnExit
      >
        <p>快讯：{selectedTitle}</p>
        <p>当前可见：{ui.visibilityLabel}</p>
        <p>操作原因：{uiInput.fields.reason || '尚未填写'}</p>
        <p>
          {detail?.publication?.visible
            ? '用户将无法再看到这条快讯，同时阻止自动发布；重新公开需要解除拦截、保存审核并发布。'
            : '拦截后不会公开，并阻止自动发布。'}
        </p>
        <p>当前编辑内容不会因此保存。</p>
      </Modal>
      <Modal
        title="核对最新审核"
        visible={compareOpen}
        onCancel={() => setCompareOpen(false)}
        footer={
          <>
            <Button onClick={() => setCompareOpen(false)}>取消</Button>
            <Button
              onClick={() => {
                onAdoptLatest();
                setCompareOpen(false);
              }}
            >
              采用最新审核
            </Button>
            <Button
              type="primary"
              onClick={() => {
                onKeepCurrent();
                setCompareOpen(false);
              }}
            >
              保留当前编辑
            </Button>
          </>
        }
        unmountOnExit
      >
        {diffs.length ? (
          <ul>
            {diffs.map(item => (
              <li key={item.field}>
                {item.label}：当前编辑「{item.current}」；最新审核「{item.latest}」
              </li>
            ))}
          </ul>
        ) : (
          <p>当前编辑与最新审核正文一致。</p>
        )}
        {detail && uiInput.detail && (
          <p>
            来源版本 {detail.sourceVersion} · 分析 {detail.analysis?.id || '无'}
            {detail.review && detail.analysis && detail.review.analysisId !== detail.analysis.id
              ? '。原文/AI 分析已更新，需要重新核对依据。'
              : ''}
          </p>
        )}
      </Modal>
    </Drawer>
  );
}
